import { h, render } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { createFocusTrap } from 'focus-trap';
import CloseIcon from './closeIcon';
const Modal = ({ onClose , children , ariaLabel , category  })=>{
    const panelRef = useRef(null);
    const trapRef = useRef(null);
    const onCloseRef = useRef(onClose);
    useEffect(()=>{
        var _document_body, _document_body_classList;
        const panel = panelRef.current;
        if (!panel) return;
        try {
            trapRef.current = createFocusTrap(panel, {
                delayInitialFocus: false,
                clickOutsideDeactivates: true,
                escapeDeactivates: true,
                onDeactivate: ()=>{
                    var _onCloseRef_current;
                    (_onCloseRef_current = onCloseRef.current) === null || _onCloseRef_current === void 0 ? void 0 : _onCloseRef_current.call(onCloseRef);
                }
            });
            trapRef.current.activate();
        } catch (err) {
            console.warn('[modal] focus-trap failed', err);
        }
        // Nudge the browser to retract the address bar before locking scroll,
        // giving the modal the maximum available viewport height.
        window.scrollTo({
            top: 1
        });
        document === null || document === void 0 ? void 0 : (_document_body = document.body) === null || _document_body === void 0 ? void 0 : (_document_body_classList = _document_body.classList) === null || _document_body_classList === void 0 ? void 0 : _document_body_classList.add('is-frozen');
        return ()=>{
            var _document_body, _document_body_classList;
            try {
                if (trapRef.current) {
                    trapRef.current.deactivate();
                    trapRef.current = null;
                }
            } catch (err) {
            // ignore
            }
            document === null || document === void 0 ? void 0 : (_document_body = document.body) === null || _document_body === void 0 ? void 0 : (_document_body_classList = _document_body.classList) === null || _document_body_classList === void 0 ? void 0 : _document_body_classList.remove('is-frozen');
            window.scrollTo({
                top: 0
            });
        };
    }, []);
    // keep the latest onClose in a ref so the trap doesn't need recreating
    useEffect(()=>{
        onCloseRef.current = onClose;
    }, [
        onClose
    ]);
    const handleBackdrop = ()=>{
        var _onCloseRef_current;
        (_onCloseRef_current = onCloseRef.current) === null || _onCloseRef_current === void 0 ? void 0 : _onCloseRef_current.call(onCloseRef);
    };
    return h('div', {
        className: 'modal',
        'data-testid': 'modal',
        role: 'dialog',
        'aria-modal': 'true',
        'aria-label': ariaLabel || 'Dialog',
        'aria-hidden': 'false'
    }, [
        h('div', {
            className: 'modal__backdrop',
            onClick: handleBackdrop
        }),
        h('div', {
            className: `modal__panel${category ? ` modal__panel--${category}` : ''}`,
            role: 'document',
            'data-category': category || undefined,
            ref: (el)=>panelRef.current = el
        }, [
            h('button', {
                className: 'modal__close',
                'data-testid': 'modal-close-button',
                type: 'button',
                'aria-label': 'Close',
                onClick: onClose
            }, h('span', {}, h(CloseIcon, {}))),
            h('div', {
                className: 'modal__content'
            }, children)
        ])
    ]);
};
export const openModal = (vnode, opts)=>{
    if (opts && opts.id) {
        const existing = document.querySelector(`div[data-modal-id="${opts.id}"]`);
        if (existing) {
            return {
                close: ()=>{
                    try {
                        render(null, existing);
                    } catch (err) {
                    // ignore
                    }
                    if (existing.parentElement) existing.parentElement.removeChild(existing);
                }
            };
        }
    }
    const container = document.createElement('div');
    if (opts && opts.id) container.setAttribute('data-modal-id', opts.id);
    document.body.appendChild(container);
    const cleanup = ()=>{
        try {
            render(null, container);
        } catch (err) {
        // ignore
        }
        if (container.parentElement) container.parentElement.removeChild(container);
    };
    const handleClose = ()=>{
        cleanup();
    };
    render(h(Modal, {
        onClose: handleClose,
        ariaLabel: opts === null || opts === void 0 ? void 0 : opts.ariaLabel,
        category: opts === null || opts === void 0 ? void 0 : opts.category
    }, vnode), container);
    // Close the modal when the global `modal:close` event is dispatched.
    // If the event provides a `detail.id`, only close modals whose `opts.id` matches.
    const globalCloseHandler = (ev)=>{
        try {
            var _ce_detail;
            const ce = ev;
            const closeId = ce === null || ce === void 0 ? void 0 : (_ce_detail = ce.detail) === null || _ce_detail === void 0 ? void 0 : _ce_detail.id;
            // Require an explicit id on the event to close a modal.
            if (!closeId) return;
            // If this modal was created with an id, only close if it matches.
            if (opts && opts.id) {
                if (String(opts.id) !== String(closeId)) return;
            } else {
                // event targets a specific modal id but this modal has none
                return;
            }
            handleClose();
        } catch (err) {
        // ignore
        }
    };
    if (typeof window !== 'undefined' && window.addEventListener) {
        window.addEventListener('modal:close', globalCloseHandler);
    }
    // Ensure the global listener is removed when the modal is cleaned up
    const originalCleanup = cleanup;
    const wrappedCleanup = ()=>{
        try {
            if (typeof window !== 'undefined' && window.removeEventListener) {
                window.removeEventListener('modal:close', globalCloseHandler);
            }
        } catch (err) {
        // ignore
        }
        originalCleanup();
    };
    // return a close that uses the wrapped cleanup so callers removing the modal
    // also remove the global listener
    return {
        close: wrappedCleanup
    };
};
export default Modal;
